/* ============================================================
   PerformerList — TASK-197: matching performers for a quote.
   Shows providers whose printers fit the job (material, build
   volume, technology, seller limits) with their price for this
   job + delivery cost. 3830fe0d (Artem 2026-08-28 P2): the
   quote page only uploads and calculates — the pick now adds a
   TASK-201 quote-print line to the cart (server re-prices via
   matching); pickup point, color and checkout live in the cart.
   ============================================================ */

import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import type { PerformerMatch, QuoteResponse } from '../types';
import { SAME_CITY_DELIVERY_RUB, deliveryMethodToJson } from '../types/product';
import type { WebDeliveryMethod } from '../types/product';
import { matchPostCaption, matchPostLines } from './postProcessingView';
import { referredBadgeText } from './referralView';

/* TASK-196: guard every displayed number — the API may deliver
   numeric enums / nulls; rendering must never crash. */
function num(value: number | null | undefined): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function formatRub(value: number | null | undefined): string {
  return num(value).toLocaleString('ru-RU', { maximumFractionDigits: 0 });
}

const TECHNOLOGY_LABELS: Record<string, string> = {
  fdm: 'FDM', '0': 'FDM',
  sla: 'SLA', '1': 'SLA',
  sls: 'SLS', '2': 'SLS',
};

function technologyLabel(tech: string | number | null | undefined): string {
  if (tech === null || tech === undefined) return '—';
  return TECHNOLOGY_LABELS[String(tech).toLowerCase()] ?? String(tech).toUpperCase();
}

type AddState =
  | { phase: 'idle' }
  | { phase: 'placing' }
  | { phase: 'done'; providerName: string }
  | { phase: 'error'; message: string };

interface PerformerListProps {
  quote: QuoteResponse;
  /** 3830fe0d: after a successful add, offers the cart continuation. */
  onGoToCart?: () => void;
}

export function PerformerList({ quote, onGoToCart }: PerformerListProps) {
  const [matches, setMatches] = useState<PerformerMatch[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [add, setAdd] = useState<AddState>({ phase: 'idle' });
  // G8-D1: delivery method chips — rides AddQuotePrintDto so the cart groups
  // the line under the right delivery cost; the point itself is chosen at
  // cart checkout.
  const [deliveryMethod, setDeliveryMethod] = useState<WebDeliveryMethod>('Courier');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setMatches(null);
    setSelectedId(null);
    setAdd({ phase: 'idle' });

    (async () => {
      try {
        const result = await api.getQuotePerformers(quote.quoteId);
        if (!cancelled) {
          setMatches(result.matches ?? []);
          if ((result.matches ?? []).length > 0) setSelectedId(result.matches[0].providerId);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Не удалось загрузить исполнителей');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [quote.quoteId]);

  const selected = useMemo(
    () => matches?.find((m) => m.providerId === selectedId) ?? null,
    [matches, selectedId],
  );

  // 3830fe0d: quote-print line into the cart — no client re-quote and no
  // direct order: AddQuotePrintAsync recomputes price/limits server-side.
  const handleAddToCart = async () => {
    if (!selected) return;
    setAdd({ phase: 'placing' });
    try {
      await api.addQuotePrint(
        quote.quoteId,
        selected.providerId,
        deliveryMethodToJson(deliveryMethod),
      );
      setAdd({ phase: 'done', providerName: selected.providerName });
    } catch (err) {
      setAdd({ phase: 'error', message: err instanceof Error ? err.message : 'Не удалось добавить в корзину' });
    }
  };

  if (loading) {
    return (
      <div className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-sm)', padding: 'var(--space-lg)' }}>
        <div className="spinner" />
        <span style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-secondary)' }}>
          Подбираем исполнителей...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card" style={{ padding: 'var(--space-md) var(--space-lg)' }}>
        <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-danger)' }}>{error}</p>
      </div>
    );
  }

  if (add.phase === 'done') {
    return (
      <div className="card" style={{ padding: 'var(--space-lg)', textAlign: 'center' }}>
        <p style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)', marginBottom: 'var(--space-xs)' }}>
          Добавлено в корзину ✓
        </p>
        <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-md)' }}>
          Печать у «{add.providerName}». В корзине выберите пункт самовывоза или адрес доставки, цвет и оформите заказ.
        </p>
        {onGoToCart && (
          <button className="btn btn-primary" onClick={onGoToCart}>
            Перейти в корзину
          </button>
        )}
      </div>
    );
  }

  if (!matches || matches.length === 0) {
    // Graceful empty state.
    return (
      <div className="card" style={{ padding: 'var(--space-lg)', textAlign: 'center' }}>
        <p style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-medium)', marginBottom: 'var(--space-xs)' }}>
          Подходящих исполнителей пока нет
        </p>
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
          Ни один принтер в базе не подходит под этот заказ (материал, габариты или ограничения продавцов).
          Попробуйте другой материал или уменьшите модель.
        </p>
      </div>
    );
  }

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
      <div>
        <p style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>
          Подходящие исполнители
        </p>
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
          {matches.length} {plural(matches.length, 'принтер подходит', 'принтера подходят', 'принтеров подходят')} под этот заказ · сначала ближайшие
        </p>
      </div>

      {matches.map((m) => {
        const isSelected = m.providerId === selectedId;
        return (
          <button
            key={`${m.providerId}-${m.printerId}`}
            type="button"
            onClick={() => setSelectedId(m.providerId)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-sm)',
              width: '100%',
              textAlign: 'left',
              cursor: 'pointer',
              borderRadius: 'var(--radius-md)',
              border: isSelected
                ? '2px solid var(--color-primary)'
                : '1px solid var(--color-separator)',
              background: 'var(--color-surface)',
              padding: 'var(--space-md) var(--space-lg)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 'var(--space-sm)' }}>
              <span style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-semibold)' }}>
                {m.providerName}
                {m.isVerified && (
                  <span style={{ color: 'var(--color-success)', marginLeft: 'var(--space-xs)' }} title="Проверенный исполнитель">
                    ✓
                  </span>
                )}
              </span>
              {/* 2f9c70ba: active referrer — suggested first, free choice kept. */}
              {referredBadgeText(m.isReferred) && (
                <span
                  style={{
                    fontSize: 'var(--fs-caption)',
                    color: 'var(--color-primary)',
                    fontWeight: 'var(--fw-medium)',
                    background: 'var(--color-secondary)',
                    borderRadius: 'var(--radius-sm)',
                    padding: '2px var(--space-xs)',
                    marginLeft: 'var(--space-xs)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {referredBadgeText(m.isReferred)}
                </span>
              )}
              {m.sameCity && (
                <span
                  style={{
                    fontSize: 'var(--fs-caption)',
                    color: 'var(--color-primary)',
                    fontWeight: 'var(--fw-medium)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Рядом с вами
                </span>
              )}
            </div>

            <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
              {m.printerModel || m.printerName} · {technologyLabel(m.technology)} · {m.city}
              {m.rating != null ? ` · ★ ${num(m.rating).toFixed(1)} (${num(m.reviewCount)})` : ''}
              {num(m.completedOrderCount) > 0 ? ` · ${num(m.completedOrderCount)} заказ(ов)` : ''}
            </p>

            {/* 00a0da87: post-processing view for this performer — paint
                applied vs «уточняется в чате» when the auto-price is unknown,
                removal by support gram, performer-priced option chips. */}
            <PostProcessingBlock match={m} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
                Печать {formatRub(m.printPriceRub)} ₽ + {/* G8-D4: live carrier
                    tariffs are quotes — label them «примерно» with the carrier;
                    static prices render exactly as before. */}
                {m.deliveryIsLiveTariff
                  ? `доставка ≈ ${formatRub(m.deliveryCostRub)} ₽ (примерно, ${m.deliveryCarrier ?? 'перевозчик'})`
                  : `доставка ${formatRub(m.deliveryCostRub)} ₽`}
              </span>
              <span
                style={{
                  fontSize: 'var(--fs-h3)',
                  fontWeight: 'var(--fw-bold)',
                  color: 'var(--color-primary)',
                  fontFamily: 'var(--font-numbers)',
                }}
              >
                {formatRub(m.totalRub)} ₽
              </span>
            </div>
          </button>
        );
      })}

      {add.phase === 'error' && (
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{add.message}</p>
      )}

      {/* G8-D1: delivery method chips — pickup is free, courier uses the static
          price table («от» — the exact cost depends on cities, shown in the
          cart). The pickup point itself is chosen at cart checkout. */}
      <div style={{ display: 'flex', gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
        <DeliveryChip
          active={deliveryMethod === 'Pickup'}
          label="Самовывоз"
          price="0 ₽"
          onClick={() => setDeliveryMethod('Pickup')}
        />
        <DeliveryChip
          active={deliveryMethod === 'Courier'}
          label="Курьер"
          price={`от ${SAME_CITY_DELIVERY_RUB} ₽`}
          onClick={() => setDeliveryMethod('Courier')}
        />
      </div>
      {deliveryMethod === 'Pickup' && (
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', margin: 0 }}>
          Пункт самовывоза и цвет печати выберете в корзине при оформлении.
        </p>
      )}

      <button
        className="btn btn-primary"
        style={{ width: '100%' }}
        disabled={!selected || add.phase === 'placing'}
        onClick={handleAddToCart}
      >
        {add.phase === 'placing' ? 'Добавляем...' : selected
          ? `В корзину — «${selected.providerName}»`
          : 'Выберите исполнителя'}
      </button>
    </div>
  );
}

/* 00a0da87: light-theme post-processing block inside a match card —
   honest per-performer outcome of the customer's toggles. */
function PostProcessingBlock({ match }: { match: PerformerMatch }) {
  const lines = matchPostLines(match.postProcessing);
  if (!lines) return null;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-xxs)',
        padding: 'var(--space-xs) var(--space-sm)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-separator)',
        background: 'var(--color-bg)',
      }}
    >
      <span style={{ fontSize: 'var(--fs-overline)', color: 'var(--color-text-tertiary)' }}>
        {matchPostCaption(match.postProcessing).toUpperCase()}
      </span>
      {lines.map((line) => (
        <div
          key={`${line.kind}-${line.text}`}
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 'var(--space-sm)' }}
        >
          <span
            style={{
              fontSize: 'var(--fs-caption)',
              fontWeight: 'var(--fw-medium)',
              color: line.kind === 'not-offered' ? 'var(--color-text-secondary)' : 'var(--color-text-primary)',
            }}
          >
            {line.text}
          </span>
          <span
            style={{
              fontSize: 'var(--fs-caption)',
              color:
                line.kind === 'chat'
                  ? 'var(--color-primary)'
                  : line.kind === 'not-offered'
                    ? 'var(--color-text-tertiary)'
                    : 'var(--color-text-secondary)',
            }}
          >
            {line.detail}
          </span>
        </div>
      ))}
    </div>
  );
}

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

/* G8-D1: light-theme method chip — selected state via the accent border,
   no gradients/heavy shadows (DESIGN-SYSTEM.md). */
function DeliveryChip({ active, label, price, onClick }: {
  active: boolean;
  label: string;
  price: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 'var(--space-xs)',
        padding: 'var(--space-xs) var(--space-md)',
        borderRadius: 'var(--radius-md)',
        border: active
          ? '2px solid var(--color-primary)'
          : '1px solid var(--color-separator)',
        background: 'var(--color-surface)',
        cursor: 'pointer',
        color: active ? 'var(--color-primary)' : 'var(--color-text-primary)',
        fontWeight: active ? 'var(--fw-semibold)' : 'var(--fw-medium)',
        fontSize: 'var(--fs-body)',
      }}
    >
      <span>{label}</span>
      <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>{price}</span>
    </button>
  );
}
